import {
  type VerificationState as LedgerState,
  type ResourcePrice,
  type StatCell,
  type TaggedModifier,
  VERIFICATION_FLAG_IDS,
  type VerificationFlagId,
} from "@sins2-atlas/game-data";

// The formula module: pure arithmetic over ADR 0003 stat cells, and the only place a
// published number is rounded. `index.ts` is the package's barrel over this and the
// page-side fold (unit-fold.ts), which builds on it.
//
// Pure domain logic for the Insight Layer. Inputs are plain data extracted from the game
// files; outputs are ADR 0003 stat cells. Nothing here reads files or guesses: every formula
// is the one documented in the column's disclosure, and anything the shipped data cannot
// decide is a Formula Unverified cell naming the observation that resolves it.

/** One weapon's compute-relevant fields, as extracted from a `.weapon` file. */
export interface WeaponInput {
  id: string;
  /** The game's own top-level `weapon_type`: planet-bombing weapons use the bombing fields
   * and cannot engage ships. */
  weaponType: "normal" | "planet_bombing";
  firingType?: "projectile" | "beam" | "spawn_torpedo";
  damage?: number;
  cooldown: number;
  range: number;
  /** Length of the weapon's TOP-LEVEL burst_pattern (the gameplay one — effects-level
   * burst patterns are visual and never reach this input). */
  burstShots?: number;
  bombingDamage?: number;
  populationDamage?: number;
  /** The active selection's modifiers per weapon quantity, applicability (weapon tags,
   * weapon_type, race) already resolved by extraction; an empty list is Base. A cell gates on
   * research_stacking exactly when a quantity its math uses carries modifiers — a
   * cooldown-only refit must not gate Nova Damage. */
  researchModifiers?: WeaponResearchModifiers;
}

export interface WeaponResearchModifiers {
  damage?: StatModifier[];
  cooldown?: StatModifier[];
  range?: StatModifier[];
  bombingDamage?: StatModifier[];
}

type WeaponQuantity = keyof WeaponResearchModifiers;

/** The current resolution of each named verification gate (HANDOFF §3.7, Issue 11).
 * Extraction owns the live value; flipping one and re-extracting is the whole release
 * process for turning the gated cells Computed. The shape is the dataset's own
 * VerificationStateSchema (schema v25 ships the ledger, ADR 0009) so the extractor's fold
 * and a page's fold read one ledger type and cannot drift; the per-gate story lives on
 * that schema and in extraction's verification.ts. */
export type VerificationState = LedgerState;

/** One modifier applicable to a stat, in the game's own vocabulary (`value_behavior`:
 * additive | scalar). Extraction resolves applicability (tags, race, prerequisites) before
 * anything reaches compute — these lists are already the unit's own.
 *
 * It is the dataset's own TaggedModifier with the source tag taken off, because that is
 * exactly what it always was: the fold arithmetic reads `behavior` and `value`, carries
 * `caption` (the Assumption Caption a Corruption Tier modifier stamps on every cell its
 * quantity feeds, ADR 0003) and `gate` (the verification flag the modifier's APPLICABILITY
 * waits on, ADR 0007), and never asks WHERE the modifier came from — deciding that is
 * unit-fold's job, one layer up. Declaring it as the Omit rather than restating the four
 * fields is what stops the two drifting: a field added to the shipped shape arrives here. */
export type StatModifier = Omit<TaggedModifier, "source">;

/** The gates a modifier list is waiting on: the shared composition rule while any modifier
 * is present, plus each modifier's own applicability gate while unresolved. */
function modifierGates(
  modifiers: StatModifier[],
  verification: VerificationState,
): VerificationFlagId[] {
  const gates: VerificationFlagId[] = [];
  if (modifiers.length > 0 && verification.research_stacking === "unverified") {
    gates.push("research_stacking");
  }
  for (const modifier of modifiers) {
    if (modifier.gate !== undefined && verification[modifier.gate] === "unverified") {
      if (!gates.includes(modifier.gate)) gates.push(modifier.gate);
    }
  }
  return gates;
}

/** The captions a modifier list stamps on the cells it feeds, first occurrence wins. */
function modifierCaptions(modifiers: StatModifier[]): string[] {
  const captions: string[] = [];
  for (const modifier of modifiers) {
    if (modifier.caption !== undefined && !captions.includes(modifier.caption)) {
      captions.push(modifier.caption);
    }
  }
  return captions;
}

/** The fold under every selection, by the sum_then_scale rule: additives join the base,
 * scalars sum before applying. */
export function foldStat(base: number, modifiers: StatModifier[]): number {
  let additive = 0;
  let scalar = 0;
  for (const modifier of modifiers) {
    if (modifier.behavior === "additive") additive += modifier.value;
    else scalar += modifier.value;
  }
  return (base + additive) * (1 + scalar);
}

/** One folded stat as a published cell: untouched stats stay Computed at their base value,
 * touched stats publish only under a resolved stacking rule — every folded number depends
 * on the inferred composition formula, so the research_stacking gate covers them all. */
export function foldStatCell(
  base: number,
  modifiers: StatModifier[],
  verification: VerificationState,
): StatCell {
  if (modifiers.length === 0) return computedCell(base, []);
  const gates = modifierGates(modifiers, verification);
  if (gates.length > 0) return unverifiedCell(gates);
  return computedCell(foldStat(base, modifiers), modifierCaptions(modifiers));
}

/** The modifiers on the quantities a cell's math uses, in quantity order. */
function quantityModifiers(weapon: WeaponInput, quantities: WeaponQuantity[]): StatModifier[] {
  return quantities.flatMap((q) => weapon.researchModifiers?.[q] ?? []);
}

/** A weapon quantity with its research modifiers folded in (identity when none apply). */
function foldedQuantity(weapon: WeaponInput, quantity: WeaponQuantity, base: number): number {
  return foldStat(base, weapon.researchModifiers?.[quantity] ?? []);
}

/** The verification gates a weapon's damage math genuinely waits on. A 1-shot burst pattern
 * is NOT gated on burst multiplicity — per-shot and per-volley give the same number — and
 * only the quantities a cell's math actually uses (foldedQuantities) gate it on stacking. */
function unresolvedDamageGates(
  weapon: WeaponInput,
  verification: VerificationState,
  foldedQuantities: WeaponQuantity[],
): VerificationFlagId[] {
  const gates: VerificationFlagId[] = [];
  if ((weapon.burstShots ?? 1) > 1 && verification.burst_multiplicity === "unverified") {
    gates.push("burst_multiplicity");
  }
  if (weapon.firingType === "spawn_torpedo" && verification.torpedo_delivery === "unverified") {
    gates.push("torpedo_delivery");
  }
  for (const gate of modifierGates(quantityModifiers(weapon, foldedQuantities), verification)) {
    if (!gates.includes(gate)) gates.push(gate);
  }
  return gates;
}

/** A Formula Unverified cell naming the gates it waits on, in VERIFICATION_FLAG_IDS order.
 * The order is canonicalised HERE, at the one place such a cell is built, rather than by each
 * producer: `modifierGates` and `unresolvedDamageGates` collect in encounter order, so two
 * cells waiting on the same two gates could otherwise list them differently and the dataset
 * would stop being byte-deterministic the moment a gate reopens. */
function unverifiedCell(gates: readonly VerificationFlagId[]): StatCell {
  const [first, ...rest] = VERIFICATION_FLAG_IDS.filter((id) => gates.includes(id));
  if (first === undefined) {
    throw new Error("compute: a Formula Unverified cell needs at least one named gate");
  }
  return { state: "formula_unverified", flags: [first, ...rest] };
}

const TORPEDO_NOMINAL_CAPTION = "nominal: torpedoes can be intercepted before impact";

/** The damage one cooldown cycle delivers, under a resolved verification state. */
function volleyDamage(
  weapon: WeaponInput,
  verification: VerificationState,
  damage: number,
): number {
  const shots = weapon.burstShots ?? 1;
  return verification.burst_multiplicity === "per_shot" ? shots * damage : damage;
}

/** Normalize away float-accumulation noise (summing 3 × 10/1.5 must equal exactly 20).
 * Six decimals is far below display precision and far above the noise floor — game values
 * carry at most three. */
function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** A Computed cell, rounded. This is the ONE place a published number is rounded — every
 * producer goes through it, so no cell rounds twice and no cell rounds not at all. Exported
 * for unit-fold's Price cells, which add a Loadout's items onto the hull's own figure. */
export function computedCell(value: number, captions: string[]): StatCell {
  const rounded = round6(value);
  const [first, ...rest] = captions;
  if (first === undefined) return { state: "computed", value: rounded };
  return { state: "computed", value: rounded, captions: [first, ...rest] };
}

/** A weapon's damage math with the gates resolved — raw, unrounded numbers. Rounding happens
 * exactly once, when a value becomes a published cell; rounding per mount and then summing
 * would compound the error (16 × round(20/1.5) drifts visibly). */
type DamageMath =
  | { kind: "gated"; gates: VerificationFlagId[] }
  | { kind: "resolved"; volley: number; captions: string[] };

function resolveDamage(
  weapon: WeaponInput,
  verification: VerificationState,
  foldedQuantities: WeaponQuantity[],
): DamageMath {
  if (weapon.damage === undefined) {
    throw new Error(`compute: normal weapon ${weapon.id} carries no damage field`);
  }
  const gates = unresolvedDamageGates(weapon, verification, foldedQuantities);
  if (gates.length > 0) return { kind: "gated", gates };
  return {
    kind: "resolved",
    volley: volleyDamage(weapon, verification, foldedQuantity(weapon, "damage", weapon.damage)),
    captions: [
      ...(weapon.firingType === "spawn_torpedo" ? [TORPEDO_NOMINAL_CAPTION] : []),
      ...modifierCaptions(quantityModifiers(weapon, foldedQuantities)),
    ],
  };
}

function damageCell(
  weapon: WeaponInput,
  verification: VerificationState,
  foldedQuantities: WeaponQuantity[],
  toValue: (volley: number) => number,
): StatCell {
  const math = resolveDamage(weapon, verification, foldedQuantities);
  if (math.kind === "gated") return unverifiedCell(math.gates);
  return computedCell(toValue(math.volley), math.captions);
}

/** Sustained damage per second of one weapon mount (CONTEXT.md: DPS). */
export function weaponDps(weapon: WeaponInput, verification: VerificationState): StatCell {
  return damageCell(
    weapon,
    verification,
    ["damage", "cooldown"],
    (volley) => volley / foldedQuantity(weapon, "cooldown", weapon.cooldown),
  );
}

/** The damage one volley delivers before the first cooldown (CONTEXT.md: Nova Damage). */
export function weaponNovaDamage(weapon: WeaponInput, verification: VerificationState): StatCell {
  return damageCell(weapon, verification, ["damage"], (volley) => volley);
}

/** One weapon quantity as a published cell: the weapon file's figure with the active
 * modifiers on that quantity folded in, gated exactly when one of them is, captioned by them.
 * The four per-battery cells below are this over one quantity each, so the weapon table's
 * Damage and Cooldown columns read by the same rule as its DPS, Nova Damage and Range (they
 * showed the raw file figures under a footnote until 2026-09-08). */
function weaponQuantityCell(
  weapon: WeaponInput,
  quantity: WeaponQuantity,
  base: number,
  verification: VerificationState,
): StatCell {
  const modifiers = quantityModifiers(weapon, [quantity]);
  const gates = modifierGates(modifiers, verification);
  if (gates.length > 0) return unverifiedCell(gates);
  return computedCell(foldedQuantity(weapon, quantity, base), modifierCaptions(modifiers));
}

/** One mount's reach under its frame (CONTEXT.md: Range). The unit's Range cell is the
 * longest of these over the mounts it counts (deriveUnitStats). */
export function weaponRange(weapon: WeaponInput, verification: VerificationState): StatCell {
  return weaponQuantityCell(weapon, "range", weapon.range, verification);
}

/** One shot's damage under its frame — per SHOT, so a burst weapon's Nova Damage is this times
 * its shots. A bomb bay has no ship damage to state; foldWeapon says so in its own words. */
export function weaponDamage(weapon: WeaponInput, verification: VerificationState): StatCell {
  if (weapon.damage === undefined) {
    throw new Error(`compute: normal weapon ${weapon.id} carries no damage field`);
  }
  return weaponQuantityCell(weapon, "damage", weapon.damage, verification);
}

/** One mount's cooldown under its frame, in seconds. */
export function weaponCooldown(weapon: WeaponInput, verification: VerificationState): StatCell {
  return weaponQuantityCell(weapon, "cooldown", weapon.cooldown, verification);
}

/** A bomb bay's planet damage per volley under its frame. */
export function weaponBombingDamage(
  weapon: WeaponInput,
  verification: VerificationState,
): StatCell {
  return weaponQuantityCell(weapon, "bombingDamage", weapon.bombingDamage ?? 0, verification);
}

/** One weapon mount on a unit. Item-gated mounts (titans/starbases) exist in the base frame
 * but fire only with an item installed — Base stats exclude them behind a caption. A
 * research-gated mount (one shipped case: the antifighter frigate's light autocannon) does
 * not exist at zero research, so Base stats exclude it the same way; the Fully-Teched
 * frame's caller includes it by omitting the flag. */
export interface Mount {
  weapon: WeaponInput;
  itemGated: boolean;
  researchGated?: boolean;
}

/** What the unit is bought with. Extraction supplies the Not Applicable reason when the game
 * charges nothing (hangar-built strikecraft) — the phrasing is game semantics, not math. */
export type CostInput =
  | {
      price: ResourcePrice;
      /** Total fabrication price of the unit's exotic components (a zero object when it costs
       * none): the exotic factory's per-exotic build price, from the unit's race's recipes,
       * summed over the counts. Cost-derived cells include it behind an Assumption Caption —
       * fabrication is the declared frame (exotics can also be looted or refined). */
      exoticsFabrication: ResourcePrice;
    }
  | { notApplicable: string };

/** Fleet supply, or the stated game fact that the unit takes none (structures, strikecraft,
 * deployables — their build blocks carry no supply_cost at all). */
export type SupplyInput = { cost: number } | { notApplicable: string };

/** The unit-side inputs the derived columns divide by. */
export interface UnitCombatInput {
  cost: CostInput;
  supply: SupplyInput;
  hull: number;
  armor: number;
  shields: number;
  /** The active selection's modifiers per HP pool, applicability already resolved by
   * extraction; an empty list is Base. Only costPerHp divides by raw HP, so only it gates on
   * these. */
  researchModifiers?: {
    hull?: StatModifier[];
    armor?: StatModifier[];
    shields?: StatModifier[];
  };
}

export interface UnitDerivedStats {
  dps: StatCell;
  novaDamage: StatCell;
  range: StatCell;
  dpsPerSupply: StatCell;
  dpsPerCost: StatCell;
  costPerHp: StatCell;
  bombingRate: StatCell;
  populationRate: StatCell;
}

/** Sum per-mount damage math on raw floats: any unresolved gate poisons the sum (a number
 * you cannot trust once is a number you cannot trust summed), captions union without
 * duplicates, and the result rounds exactly once via computedCell. */
function sumDamage(
  weapons: WeaponInput[],
  verification: VerificationState,
  foldedQuantities: WeaponQuantity[],
  toValue: (weapon: WeaponInput, volley: number) => number,
): StatCell {
  const flags = new Set<VerificationFlagId>();
  const captions: string[] = [];
  let total = 0;
  for (const weapon of weapons) {
    const math = resolveDamage(weapon, verification, foldedQuantities);
    if (math.kind === "gated") {
      for (const gate of math.gates) flags.add(gate);
      continue;
    }
    total += toValue(weapon, math.volley);
    for (const caption of math.captions) {
      if (!captions.includes(caption)) captions.push(caption);
    }
  }
  if (flags.size > 0) return unverifiedCell([...flags]);
  return computedCell(total, captions);
}

/** Divide a computed cell by a plain denominator, preserving state and captions. */
function ratioCell(numerator: StatCell, map: (value: number) => number): StatCell {
  if (numerator.state !== "computed") return numerator;
  return computedCell(map(numerator.value), [...(numerator.captions ?? [])]);
}

/** All derived columns for one unit (Issue 03's Insight Layer set). */
export function deriveUnitStats(
  mounts: Mount[],
  unit: UnitCombatInput,
  verification: VerificationState,
): UnitDerivedStats {
  const included = mounts
    .filter((m) => !m.itemGated && m.researchGated !== true)
    .map((m) => m.weapon);
  // Zero-damage "weapons" exist (two Advent psi-ball effect mounts, damage 0, range 30000):
  // they contribute nothing to damage, and counting their range would fake the Range column,
  // so the unit aggregates skip them. The per-weapon breakdown still lists them honestly.
  const shipWeapons = included.filter((w) => w.weaponType === "normal" && w.damage !== 0);
  const bombingWeapons = included.filter((w) => w.weaponType === "planet_bombing");
  // The gated-mounts caption is per weapon CATEGORY: a cell carries it exactly when a gated
  // mount of the kind that feeds that cell was dropped — the value is true of the hull as
  // built, and the caption says so out loud (ADR 0003 Assumption Caption). A gated beam must
  // not caption the bombing columns, and a gated bomb bay must not caption DPS.
  const gatedCaption = (kind: "normal" | "planet_bombing"): string[] => [
    ...(mounts.some((m) => m.itemGated && m.weapon.weaponType === kind)
      ? ["excludes item-gated weapon mounts"]
      : []),
    ...(mounts.some((m) => !m.itemGated && m.researchGated === true && m.weapon.weaponType === kind)
      ? ["excludes research-gated weapon mounts"]
      : []),
  ];
  const withCaptions = (cell: StatCell, captions: string[]): StatCell =>
    cell.state === "computed"
      ? computedCell(cell.value, [...(cell.captions ?? []), ...captions])
      : cell;

  const shipGatedCaptions = gatedCaption("normal");
  // The reason must hold for every hull that lands here: truly weaponless, bombing-only
  // (its mount cannot target ships), and zero-damage-effect-only (its mount CAN target
  // ships but deals nothing — "damage", not "target", keeps the sentence true; review
  // findings 2026-07-27).
  const unarmed: StatCell = {
    state: "not_applicable",
    reason: "no weapons that can damage ships",
  };
  const armed = shipWeapons.length > 0;
  const dps = armed
    ? withCaptions(
        sumDamage(
          shipWeapons,
          verification,
          ["damage", "cooldown"],
          (w, volley) => volley / foldedQuantity(w, "cooldown", w.cooldown),
        ),
        shipGatedCaptions,
      )
    : unarmed;
  const novaDamage = armed
    ? withCaptions(
        sumDamage(shipWeapons, verification, ["damage"], (_w, volley) => volley),
        shipGatedCaptions,
      )
    : unarmed;
  // Range is the max over mounts, so only the mounts that DECIDE it may caption or gate it
  // (review round 2026-09-05): a scoped tier modifier on a shorter mount moved nothing the
  // cell shows. A mount decides the max when its folded range attains it, or when it would
  // attain it without its unresolved-gate modifiers (an open gate leaves both readings
  // open, so the value is uncertain either way). Folded ranges can be fractional
  // (10000 × 1.15), so the cell rounds via computedCell.
  const rangeOf = (w: WeaponInput) => foldedQuantity(w, "range", w.range);
  const maxRange = armed ? Math.max(...shipWeapons.map(rangeOf)) : 0;
  const leading = shipWeapons.filter((w) => rangeOf(w) === maxRange);
  const openGated = (m: StatModifier) =>
    m.gate !== undefined && verification[m.gate] === "unverified";
  const contending = shipWeapons.filter(
    (w) =>
      rangeOf(w) === maxRange ||
      foldStat(
        w.range,
        quantityModifiers(w, ["range"]).filter((m) => !openGated(m)),
      ) > maxRange,
  );
  const rangeGates = modifierGates(
    contending.flatMap((w) => quantityModifiers(w, ["range"])),
    verification,
  );
  const range = !armed
    ? unarmed
    : rangeGates.length > 0
      ? unverifiedCell(rangeGates)
      : withCaptions(
          computedCell(
            maxRange,
            modifierCaptions(leading.flatMap((w) => quantityModifiers(w, ["range"]))),
          ),
          shipGatedCaptions,
        );

  const bombingGatedCaptions = gatedCaption("planet_bombing");
  // The reason must name the right gate: an item-gated bomb bay is not a research-locked
  // one, and a hull with neither carries none at all.
  const noBombing: StatCell = mounts.some(
    (m) => m.itemGated && m.weapon.weaponType === "planet_bombing",
  )
    ? { state: "not_applicable", reason: "its planet-bombing weapon requires an item" }
    : mounts.some((m) => m.researchGated === true && m.weapon.weaponType === "planet_bombing")
      ? { state: "not_applicable", reason: "its planet-bombing weapon requires research" }
      : { state: "not_applicable", reason: "carries no planet-bombing weapon" };
  // Both bombing rates divide by the cooldown, but only bombingRate uses the bombing damage —
  // each cell gates on exactly the quantities its own math folds.
  const bombingCell = (
    quantities: WeaponQuantity[],
    perWeapon: (w: WeaponInput) => number,
  ): StatCell => {
    const modifiers = bombingWeapons.flatMap((w) => quantityModifiers(w, quantities));
    const gates = modifierGates(modifiers, verification);
    return gates.length > 0
      ? unverifiedCell(gates)
      : withCaptions(
          computedCell(
            bombingWeapons.reduce((sum, w) => sum + perWeapon(w), 0),
            modifierCaptions(modifiers),
          ),
          bombingGatedCaptions,
        );
  };
  const bombingRate =
    bombingWeapons.length === 0
      ? noBombing
      : bombingCell(
          ["bombingDamage", "cooldown"],
          (w) =>
            foldedQuantity(w, "bombingDamage", w.bombingDamage ?? 0) /
            foldedQuantity(w, "cooldown", w.cooldown),
        );
  const populationRate =
    bombingWeapons.length === 0
      ? noBombing
      : bombingCell(
          ["cooldown"],
          (w) => (w.populationDamage ?? 0) / foldedQuantity(w, "cooldown", w.cooldown),
        );

  const hpMods = unit.researchModifiers;
  const hpGates = modifierGates(
    [...(hpMods?.hull ?? []), ...(hpMods?.armor ?? []), ...(hpMods?.shields ?? [])],
    verification,
  );
  const rawHp =
    foldStat(unit.hull, hpMods?.hull ?? []) +
    foldStat(unit.armor, hpMods?.armor ?? []) +
    foldStat(unit.shields, hpMods?.shields ?? []);
  const dpsPerSupply = ((): StatCell => {
    if ("notApplicable" in unit.supply) {
      return { state: "not_applicable", reason: unit.supply.notApplicable };
    }
    const supplyCost = unit.supply.cost;
    if (supplyCost === 0) return { state: "not_applicable", reason: "costs no supply" };
    return ratioCell(dps, (v) => v / supplyCost);
  })();

  // The two cost-derived cells, resolved together: both divide by the same total, so both
  // become Not Applicable for the same two reasons and neither can be decided without it.
  // Returning just this pair keeps the function's eight-key result written once — the three
  // early returns it replaced each restated all eight, differing only in these two.
  const costCells = ((): { dpsPerCost: StatCell; costPerHp: StatCell } => {
    if ("notApplicable" in unit.cost) {
      const uncosted: StatCell = { state: "not_applicable", reason: unit.cost.notApplicable };
      return { dpsPerCost: uncosted, costPerHp: uncosted };
    }
    const { price, exoticsFabrication } = unit.cost;
    const fabricationTotal =
      exoticsFabrication.credits + exoticsFabrication.metal + exoticsFabrication.crystal;
    const totalCost = price.credits + price.metal + price.crystal + fabricationTotal;
    // The ability-created deployables declare an EMPTY price (build_time 0, no resources):
    // a per-cost ratio over zero would be Infinity, and "0 cost per HP" reads as a bargain
    // rather than the game fact that they are not bought at all.
    if (totalCost === 0) {
      const uncosted: StatCell = { state: "not_applicable", reason: "costs no resources" };
      return { dpsPerCost: uncosted, costPerHp: uncosted };
    }
    const exoticCaptions =
      fabricationTotal > 0 ? ["exotic components counted at their fabrication cost"] : [];
    const withExoticCaption = (cell: StatCell): StatCell =>
      cell.state === "computed"
        ? computedCell(cell.value, [...(cell.captions ?? []), ...exoticCaptions])
        : cell;
    // No zero-denominator guard here, unlike the totalCost === 0 case above: rawHp cannot be
    // zero. Every unit declares max_hull_points and extraction's schema types it
    // `z.number().positive()`, so a patch shipping a pool-less hull fails at parse time with
    // the file named rather than reaching this division. Armor and shields may legitimately be
    // zero; hull may not.
    return {
      dpsPerCost: withExoticCaption(ratioCell(dps, (v) => (v / totalCost) * 1000)),
      costPerHp:
        hpGates.length > 0
          ? unverifiedCell(hpGates)
          : withExoticCaption(computedCell(totalCost / rawHp, [])),
    };
  })();

  return { dps, novaDamage, range, dpsPerSupply, ...costCells, bombingRate, populationRate };
}

// --- The mitigation pipeline (Issue 09): the Art of War formula behind the damage gate ---

/** The game's one mitigation coefficient — `damage_scalar_per_additive_defensive_value`
 * at unit.uniforms:43. Extraction validates the shipped file still declares this value on
 * every run, so a patch changing it fails extraction loudly instead of silently corrupting
 * every mitigation cell. */
export const DAMAGE_SCALAR_PER_DEFENSE_POINT = 0.01;

/** How much raw damage one pool point absorbs at the given defensive value: the reciprocal
 * of the pipeline's damage scalar 1/(1 + 0.01·points). */
function absorptionFactor(points: number): number {
  return 1 + DAMAGE_SCALAR_PER_DEFENSE_POINT * points;
}

/** The mitigation pipeline's defender-side inputs under one selection. Absent durability is the honest gap Issue 03 modeled (seven roster hulls carry no field
 * and the game declares no default); absent armor strength is legal only alongside an
 * empty armor pool — probed true across all 134 fixture units. */
export interface MitigationInput {
  hull: number;
  armor: number;
  shields: number;
  durability?: number;
  armorStrength?: number;
  /** The active selection's modifiers, applicability already resolved by extraction; an
   * empty list is Base. */
  researchModifiers?: {
    hull?: StatModifier[];
    armor?: StatModifier[];
    shields?: StatModifier[];
    armorStrength?: StatModifier[];
  };
}

/** The Effective HP caption — exported so the disclosure the web app shows beside the figure
 * is the same string the cell carries, never a restatement. */
export const EHP_CAPTION = "vs 0 Pierce";

/** Total damage a unit absorbs before destruction at 0 Pierce (CONTEXT.md: Effective HP).
 * Every pool absorbs ×(1 + 0.01·Durability); the armor pool additionally
 * ×(1 + 0.01·Armor Strength) — the second, Pierce-immune scalar. Pierce-dependent by
 * definition, so the computed cell always carries the declared-frame caption. */
export function effectiveHp(input: MitigationInput, verification: VerificationState): StatCell {
  if (input.durability === undefined) {
    return {
      state: "not_in_game_data",
      reason: "effective HP needs durability, and the unit file carries no durability field",
    };
  }
  const mods = input.researchModifiers;
  const hull = foldStat(input.hull, mods?.hull ?? []);
  const armor = foldStat(input.armor, mods?.armor ?? []);
  const shields = foldStat(input.shields, mods?.shields ?? []);
  // Whether the armor pool is in play is stacking-rule-invariant (scalars cannot zero a
  // positive base, and pruneForBase strips scalar-only lists off a zero one), so folding
  // before the gate check is safe — and armor strength touches this cell's math, and
  // therefore its gates, only while the pool exists (the per-quantity gating rule).
  const armorInPlay = armor > 0;
  const gates = modifierGates(
    [
      ...(mods?.hull ?? []),
      ...(mods?.armor ?? []),
      ...(mods?.shields ?? []),
      ...(armorInPlay ? (mods?.armorStrength ?? []) : []),
    ],
    verification,
  );
  if (verification.damage_formula === "unverified") gates.push("damage_formula");
  if (gates.length > 0) return unverifiedCell(gates);
  const durabilityFactor = absorptionFactor(input.durability);
  if (!armorInPlay) return computedCell((hull + shields) * durabilityFactor, [EHP_CAPTION]);
  if (input.armorStrength === undefined) {
    throw new Error(
      "compute: a unit with an armor pool must declare armor_strength — the extraction invariant broke",
    );
  }
  const armorStrength = foldStat(input.armorStrength, mods?.armorStrength ?? []);
  return computedCell(
    (hull + shields) * durabilityFactor +
      armor * durabilityFactor * absorptionFactor(armorStrength),
    [EHP_CAPTION],
  );
}

/** The fraction of raw damage that survives mitigation for one attacker–defender pair,
 * per defender pool (CONTEXT.md: Damage Efficiency). Pierce negates Durability — never
 * below zero, and never against Armor Strength. Inputs are one frame's already-folded
 * numbers, so only the formula gate applies — and the parameter says so: asking for the one
 * flag this reads keeps callers from having to invent values for the other three (a full
 * VerificationState still satisfies it).
 *
 * **Nothing renders this today.** Its consumer was the Counters section, withdrawn 2026-09-07
 * pending the user's re-think of what it should say (CLAUDE.md open item 1); the data the
 * section needs still ships, so it can return without a re-extract. The arithmetic is kept
 * here under its tests for exactly that — deleting and re-deriving it is how a verified
 * formula turns back into an unverified one (CONTEXT.md: Damage Efficiency). */
export function damageEfficiency(
  pierce: number,
  defender: { durability: number; armorStrength: number },
  verification: Pick<VerificationState, "damage_formula">,
): { shields: StatCell; armor: StatCell; hull: StatCell } {
  if (verification.damage_formula === "unverified") {
    const gated = unverifiedCell(["damage_formula"]);
    return { shields: gated, armor: gated, hull: gated };
  }
  const effective = Math.max(defender.durability - pierce, 0);
  const scalar = 1 / absorptionFactor(effective);
  return {
    shields: computedCell(scalar, []),
    armor: computedCell(scalar / absorptionFactor(defender.armorStrength), []),
    hull: computedCell(scalar, []),
  };
}
